import { AnchorIcon } from "../../../shared/Icons";
import { vesselCardStyles } from "../constants/vessel-card-styles.constants";
import { VesselCardCurrentPerformance } from "../models/vessel-card.models";
import { CalendarDaysIcon, ChartBarIcon } from "@heroicons/react/24/outline";
import { twMerge } from "tailwind-merge";

interface VesselCardCurrentPerformancePanelProps {
  performance: VesselCardCurrentPerformance;
}

const formatMetric = (value: number | null): string => (value === null ? "—" : value.toFixed(1));

const performanceToneClassNames = {
  positive: { text: "text-emerald-600", dot: "bg-emerald-500" },
  warning: { text: "text-amber-600", dot: "bg-amber-500" },
  neutral: { text: "text-gray-600 dark:text-gray-300", dot: "bg-gray-400" },
  negative: { text: "text-red-600", dot: "bg-red-500" },
};

const VesselCardCurrentPerformancePanel = ({ performance }: VesselCardCurrentPerformancePanelProps) => (
  <section data-test="vessel-card-current-performance" className={twMerge(vesselCardStyles.surface, "xl:col-span-2")}>
    <div className="flex items-center gap-2">
      <span className="rounded-full bg-primary-20 p-2 text-green dark:bg-teal-950/60 dark:text-teal-200">
        <ChartBarIcon className="h-5 w-5" />
      </span>
      <h2
        className={vesselCardStyles.sectionTitle}
        title="Current hull performance compared with expected clean-hull performance using available operational data."
      >
        Current performance
      </h2>
    </div>

    <p className={twMerge("mt-5 text-sm font-semibold", performanceToneClassNames[performance.tone].text)}>
      <span
        className={twMerge(
          "mr-2 inline-block h-2.5 w-2.5 rounded-full",
          performanceToneClassNames[performance.tone].dot,
        )}
      />
      {performance.label}
    </p>
    {performance.speedDifference !== null && (
      <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">by {formatMetric(performance.speedDifference)} kn</p>
    )}

    <dl className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
      <div className="rounded-lg bg-gray-50 p-3 text-center dark:bg-gray-900/50">
        <CalendarDaysIcon className="mx-auto h-5 w-5 text-green" />
        <dt className={twMerge(vesselCardStyles.fieldLabel, "mt-2")}>Last hull inspection / cleaning</dt>
        <dd className={twMerge(vesselCardStyles.fieldValue, "mt-1")}>
          {performance.lastHullInspectionOrCleaning ?? "—"}
        </dd>
      </div>
      <div className="rounded-lg bg-gray-50 p-3 text-center dark:bg-gray-900/50">
        <AnchorIcon className="mx-auto h-5 w-5 text-green" />
        <dt className={twMerge(vesselCardStyles.fieldLabel, "mt-2")}>Last dry dock</dt>
        <dd className={twMerge(vesselCardStyles.fieldValue, "mt-1")} data-test="vessel-card-last-dry-dock">
          {performance.lastDryDock ?? "Not available"}
        </dd>
      </div>
    </dl>
  </section>
);

export default VesselCardCurrentPerformancePanel;
